<template>
  <div class="budget-list">
    <!-- 费用预算（企业微信手机端） -->
    <mobile-list-header>
      <!-- 第一行：搜索框占满整行，左输入框 + 右搜索按钮 -->
      <div class="search-bar">
        <div class="search-input">
          <yq-icon class="icon" name="element-ui_search"/>
          <input v-model="query.searchKey" placeholder="预算编号/预算人" @keyup.enter="resetSearch"/>
          <yq-icon v-if="query.searchKey" class="icon clear" name="element-ui_close" @click="clearKey"/>
        </div>
        <div class="search-btn" @click="resetSearch">搜索</div>
      </div>
      <!-- 第二行：查询条件，无背景色，超过一屏横向滑动 -->
      <div class="filter-row">
        <!-- 数据模式：显示当前模式，点一下在「列表模式 / 报表模式」之间切换 -->
        <div class="mode-chip" @click="switchMode()">{{ modeLabel }}</div>
        <!-- 预算维度：年/季/月，切换时清掉另一层级的定位条件 -->
        <mobile-condition-select v-model="query.budgetType" :data="typeOptions"
                                 label-column="label" code-column="value" type="list"
                                 placeholder="预算维度" @change="onDimensionChange"/>
        <mobile-condition-select v-model="query.budgetYear" :data="yearOptions"
                                 label-column="label" code-column="value" type="list"
                                 placeholder="预算年度" @change="resetSearch"/>
        <mobile-condition-select v-if="query.budgetType === 2" v-model="query.budgetQuarter" :data="quarterOptions"
                                 label-column="label" code-column="value" type="list"
                                 placeholder="季度" @change="resetSearch"/>
        <mobile-condition-select v-if="query.budgetType === 3" v-model="query.budgetMonth" :data="monthOptions"
                                 label-column="label" code-column="value" type="list"
                                 placeholder="月份" @change="resetSearch"/>
        <mobile-condition-select v-model="query.departCode" :data="departTree"
                                 label-column="departName" code-column="departCode" type="tree"
                                 placeholder="部门" @change="resetSearch"/>
        <mobile-search-select v-model="query.employeeId" :query="queryEmployeePage" size="small"
                              label-column="employeeName" code-column="id"
                              placeholder="人员" search-placeholder="职员名称/手机号"
                              @change="resetSearch">
          <!-- 职员按「头像 + 名称 / 手机号」两行显示（预览模板 B） -->
          <template #item="{item}">
            <div class="mss-item mss-item-avatar-row">
              <div class="mss-item-avatar">
        <img v-if="item.iconUrl" :src="$getImageUrl(item.iconUrl)" alt=""/>
        <yq-icon v-else name="element-ui_user"/>
              </div>
              <div class="mss-item-info">
        <div class="mss-item-main">{{ item.employeeName }}</div>
        <div class="mss-item-sub">{{ item.employeePhone || "无联系电话" }}</div>
              </div>
            </div>
          </template>
        </mobile-search-select>
      </div>
    </mobile-list-header>

    <!-- ==================== 列表模式：预算明细，可点进详情 ==================== -->
    <mobile-list v-if="mode === 'list'" :loading="loading" :has-more="searchInfo.hasMore"
                 :query-list="queryList" :refresh="resetSearch" ref="mobileList">
      <list-card class="budget-item" v-for="item in dataList" :key="item.id" :label="item.budgetName" @click="toDetail(item)">
        <template #desc>
          <span class="type-tag" :class="'tag-' + typeTag(item.budgetType)">{{ typeLabel(item.budgetType) }}</span>
          <span class="status-tag" :class="'tag-' + statusTagType(item.budgetStatus)">{{ item.budgetStatus }}</span>
          <yq-icon class="item-arrow" name="element-ui_arrow-right"/>
        </template>
        <data-column label="预算编号">{{ item.budgetNo || "—" }}</data-column>
        <data-column label="预算人">{{ item.employeeName || "—" }}</data-column>
        <data-column label="所属部门">{{ item.departTitle || "—" }}</data-column>
        <data-column label="预算年度">{{ budgetPeriod(item) }}</data-column>
        <data-column label="预算金额">{{ money(item.budgetAmount) }}</data-column>
        <data-column label="已使用">{{ money(item.usedAmount) }}</data-column>
        <data-column label="执行进度">
          <div class="progress">
            <span class="track"><i :style="{width: clampedRate(item.usedRate), background: barColor(item.usedRate)}"></i></span>
            <span class="pct">{{ rateText(item.usedRate) }}</span>
          </div>
        </data-column>
      </list-card>
    </mobile-list>

    <!-- ==================== 报表模式：部门预算汇总，仅查看不可点 ==================== -->
    <div v-else class="report-body">
      <div class="stat-grid">
        <div class="stat-card">
          <div class="label">预算总额</div>
          <div class="value money">{{ money(report.budgetAmount) }}</div>
        </div>
        <div class="stat-card">
          <div class="label">已使用金额</div>
          <div class="value money">{{ money(report.usedAmount) }}</div>
        </div>
        <div class="stat-card">
          <div class="label">可用余额</div>
          <div class="value money" :class="{neg: Number(report.balanceAmount) < 0}">{{ money(report.balanceAmount) }}</div>
        </div>
        <div class="stat-card">
          <div class="label">超支对象</div>
          <div class="value neg">{{ report.overCount || 0 }}<span class="unit">个</span></div>
        </div>
      </div>
      <!-- 口径说明：两个视角必然重复计算，不写清楚会被当成 bug -->
      <div class="tip-bar">
        口径说明：同一笔预算会同时计入「所在部门」与「本人」，因此部门费用合计与人员费用合计不相等，
        这是两个视角的差异，不是数据错误。预算按发生年度归集，已使用金额只算已通过的报销单。
      </div>
      <div class="report-title">部门预算汇总（{{ departRows.length }}）</div>
      <list-card class="report-item" v-for="row in departRows" :key="row.dimKey" :label="row.dimTitle || row.dimKey || '未分配'">
        <template #desc>
          <span class="status-tag" :class="'tag-' + statusTagType(row.budgetStatus)">{{ row.budgetStatus }}</span>
        </template>
        <data-column label="预算金额">{{ money(row.budgetAmount) }}</data-column>
        <data-column label="已使用">{{ money(row.usedAmount) }}</data-column>
        <data-column label="可用余额">{{ money(row.balanceAmount) }}</data-column>
        <data-column label="执行进度">
          <div class="progress">
            <span class="track"><i :style="{width: clampedRate(row.usedRate), background: barColor(row.usedRate)}"></i></span>
            <span class="pct">{{ rateText(row.usedRate) }}</span>
          </div>
        </data-column>
        <data-column label="预算条数">{{ row.budgetCount || 0 }}</data-column>
      </list-card>
      <div v-if="!departRows.length" class="report-empty">暂无部门预算数据</div>
    </div>

    <!-- 悬浮「新增预算」按钮：只有列表模式才显示（报表模式是只读汇总） -->
    <mobile-float-button v-if="mode === 'list'" label="新增预算" icon="element-ui_plus" @click="toCreate"/>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import MobileList from "@/components/qywx/layout/MobileList.vue";
import MobileListHeader from "@/components/qywx/layout/MobileListHeader.vue";
import MobileConditionSelect from "@/components/qywx/data/MobileConditionSelect.vue";
import MobileSearchSelect from "@/components/qywx/data/MobileSearchSelect.vue";
import MobileFloatButton from "@/components/qywx/button/MobileFloatButton.vue";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import YqIcon from "@/components/YqIcon.vue";
import expenseBudgetApi from "@/api/dz-crm/expense/expenseBudgetApi";
import commonApi from "@/api/commonApi";

/** 预算类型，与后端 BudgetNode.TYPE_* 一一对应 */
const TYPE_LABEL = {1: "年度", 2: "季度", 3: "月度"};
const TYPE_TAG = {1: "primary", 2: "success", 3: "info"};
/** 执行状态 → 标签色 */
const STATUS_TAG = {"正常": "success", "预警": "warning", "超支": "danger"};

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "EB",
  components: {
    YqIcon, DataColumn, ListCard, MobileFloatButton, MobileSearchSelect, MobileConditionSelect,
    MobileListHeader, MobileList
  },
  data() {
    const thisYear = new Date().getFullYear();
    return {
      // 数据模式：list 列表模式（预算明细）/ report 报表模式（部门预算汇总）
      mode: "list",
      loading: false,
      // 列表模式的分页
      searchInfo: {
        pageNumber: 0,
        pageSize: 20,
        hasMore: true, // 初始为 true，列表挂载后由无限滚动自动触发首次查询
      },
      // 查询条件（两种模式共用）
      query: {
        searchKey: "",
        // 默认看年预算（层级最高，数最少最直观），与 web 端一致
        budgetType: 1,
        budgetYear: thisYear,
        budgetQuarter: null,
        budgetMonth: null,
        departCode: "",
        employeeId: "",
      },
      dataList: [],
      // 报表模式：KPI + 部门汇总
      report: {},
      typeOptions: [
        {label: "年预算", value: 1},
        {label: "季度预算", value: 2},
        {label: "月预算", value: 3},
      ],
      yearOptions: [thisYear + 1, thisYear, thisYear - 1].map(y => ({label: String(y), value: y})),
      quarterOptions: [1, 2, 3, 4].map(q => ({label: "第" + "一二三四"[q - 1] + "季度", value: q})),
      monthOptions: Array.from({length: 12}, (_, i) => ({label: (i + 1) + "月", value: i + 1})),
      departTree: [],
    };
  },
  computed: {
    departRows() {
      return this.report.departRows || [];
    },
    // chip 上显示的是「当前」模式，点一下切到另一个
    modeLabel() {
      return this.mode === "list" ? "列表模式" : "报表模式";
    }
  },
  created() {
    // 部门筛选：部门树是通用的组织架构数据，走 commonApi
    commonApi.queryDepartList().then(list => this.departTree = list || []);
  },
  methods: {
    // ---------- 查询 ----------
    queryList() {
      this.loading = true;
      this.searchInfo.pageNumber++;
      expenseBudgetApi.queryList({
        ...this.cleanQuery(),
        pageNumber: this.searchInfo.pageNumber,
        pageSize: this.searchInfo.pageSize,
      }).then(res => {
        this.dataList = this.dataList.concat(res.records || []);
        this.searchInfo.hasMore = res.pageCount > this.searchInfo.pageNumber;
        this.loading = false;
        this.$refs.mobileList.hideRefreshTitle();
      }).catch(() => {
        this.loading = false;
      })
    },
    // 报表模式：KPI + 部门预算汇总
    loadReport() {
      expenseBudgetApi.page(this.cleanQuery()).then(res => {
        this.report = res || {};
      });
    },
    /** 空值不提交，避免空串绑到后端 Integer / 影响 LIKE 条件 */
    cleanQuery() {
      const params = {};
      Object.keys(this.query).forEach(key => {
        const value = this.query[key];
        if (value === "" || value === null || value === undefined) {
          return;
        }
        params[key] = value;
      });
      return params;
    },
    // 重置查询条件，从第一页开始查（两种模式都要刷）
    resetSearch() {
      if (this.mode === "list") {
        this.searchInfo.pageNumber = 0;
        this.dataList = [];
        this.searchInfo.hasMore = true;
        this.queryList();
      } else {
        this.loadReport();
      }
    },
    clearKey() {
      this.query.searchKey = "";
      this.resetSearch();
    },
    // 不传参就是「切到另一个模式」
    switchMode(mode) {
      const next = mode || (this.mode === "list" ? "report" : "list");
      if (this.mode === next) {
        return;
      }
      this.mode = next;
      this.resetSearch();
    },
    /** 切换查询维度：季度/月份筛选只属于各自的维度，换维度时要清掉，否则会带上无效条件 */
    onDimensionChange() {
      this.query.budgetQuarter = null;
      this.query.budgetMonth = null;
      this.resetSearch();
    },
    queryEmployeePage(params) {
      return commonApi.queryEmployeeListByPage(params);
    },
    // ---------- 展示 ----------
    typeLabel(budgetType) {
      return TYPE_LABEL[budgetType] || "—";
    },
    typeTag(budgetType) {
      return TYPE_TAG[budgetType] || "info";
    },
    statusTagType(status) {
      return STATUS_TAG[status] || "info";
    },
    /** 预算节点：年度 + 季度/月份，比单看年度更能定位到具体节点 */
    budgetPeriod(item) {
      if (!item.budgetYear) {
        return "—";
      }
      if (Number(item.budgetType) === 2 && item.budgetQuarter) {
        return item.budgetYear + " 第" + "一二三四"[item.budgetQuarter - 1] + "季度";
      }
      if (Number(item.budgetType) === 3 && item.budgetMonth) {
        return item.budgetYear + "-" + String(item.budgetMonth).padStart(2, "0");
      }
      return String(item.budgetYear);
    },
    clampedRate(rate) {
      const value = Number(rate || 0);
      return Math.max(0, Math.min(100, value)).toFixed(1) + "%";
    },
    rateText(rate) {
      return Number(rate || 0).toFixed(1) + "%";
    },
    /** 进度条颜色：超支红、预警橙、正常蓝 */
    barColor(rate) {
      const value = Number(rate || 0);
      if (value > 100) {
        return "#e5484d";
      }
      return value >= 85 ? "#faad14" : "#1677ff";
    },
    money(value) {
      if (value === null || value === undefined || value === "") {
        return "0.00";
      }
      return "¥ " + Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
    toDetail(data) {
      this.router.push(`/qywx/crm/budget/detail?id=${data.id}`);
    },
    // 新增预算（详情页不带 id 即编辑模式）
    toCreate() {
      this.router.push("/qywx/crm/budget/detail");
    }
  }
};
</script>

<style lang="scss" scoped>
.budget-list{
  height: 100%;
  display: flex;
  flex-direction: column;
  // 悬浮按钮的定位祖先
  position: relative;
}
// 第一行：搜索框占满整行，左输入框 + 右搜索按钮
.search-bar{
  display: flex;
  align-items: center;
  height: 44px;
  padding-left: 12px;
  border-radius: 999px;
  background: var(--yq-background-color);
  overflow: hidden;
  .search-input{
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0;
    height: 100%;
    padding-right: 12px;
    .icon{
      flex: none;
      color: var(--yq-font-color-desc);
      font-size: var(--yq-font-size-mini-mobile);
      &.clear{
        margin-left: 6px;
      }
    }
    input{
      flex: 1;
      min-width: 0;
      height: 100%;
      margin-left: 6px;
      border: none;
      outline: none;
      background: transparent;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-base);
      &::placeholder{
        color: var(--yq-font-color-desc);
      }
    }
  }
  .search-btn{
    flex: none;
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 18px;
    background: var(--el-color-primary);
    color: #FFF;
    font-size: var(--yq-font-size-mini-mobile);
  }
}
// 第二行：查询条件，无背景色，超过一屏横向滑动
// 数据模式：一个 chip 显示当前模式，点一下切换
// ★ 用主题色：它不是普通查询条件，而是「换个视角看同一份数据」，颜色上区分开
.mode-chip{
  font-size: 12px;
  color: var(--el-color-primary);
}
// 查询条件行
.filter-row{
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 10px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar{
    display: none;
  }
  > *{
    flex: none;
  }
}
// 类型 / 状态标签
.type-tag,
.status-tag{
  flex: none;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: var(--yq-font-size-mini-mobile);
}
.tag-primary{
  color: var(--el-color-primary);
  background: var(--el-color-primary-light-9);
}
.tag-info{
  color: var(--el-color-info);
  background: var(--el-fill-color-light);
}
.tag-warning{
  color: var(--el-color-warning);
  background: var(--el-color-warning-light-9);
}
.tag-success{
  color: var(--el-color-success);
  background: var(--el-color-success-light-9);
}
.tag-danger{
  color: var(--el-color-danger);
  background: var(--el-color-danger-light-9);
}
.budget-item,
.report-item{
  :deep(.card-title .desc){
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .item-arrow{
    color: var(--yq-font-color-desc);
    font-size: var(--yq-font-size-mini-mobile);
  }
  // 执行进度条
  .progress{
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    .track{
      flex: none;
      width: 90px;
      height: 8px;
      border-radius: 4px;
      background: var(--yq-background-color);
      overflow: hidden;
      i{
        display: block;
        height: 100%;
        border-radius: 4px;
      }
    }
    .pct{
      color: var(--yq-font-color-desc);
    }
  }
}
// 报表模式：整块可滚动
.report-body{
  flex: 1;
  overflow-y: auto;
  padding: 0 10px 10px;
  .stat-grid{
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    padding-top: 10px;
    .stat-card{
      background: #FFF;
      border-radius: 6px;
      padding: 10px 12px;
      box-shadow: 0 0 10px var(--yq-shadow);
      .label{
        font-size: var(--yq-font-size-mini-mobile);
        color: var(--yq-font-color-desc);
      }
      .value{
        margin-top: 4px;
        font-size: var(--yq-font-size-large-mobile);
        font-weight: 700;
        color: var(--yq-font-color-base);
        word-break: break-all;
        &.money{
          font-size: var(--yq-font-size-small-mobile);
        }
        &.neg{
          color: #e5484d;
        }
        .unit{
          margin-left: 2px;
          font-size: var(--yq-font-size-mini-mobile);
          font-weight: 400;
        }
      }
    }
  }
  .tip-bar{
    margin-top: 10px;
    padding: 10px 12px;
    border-radius: 6px;
    background: #FAFBFC;
    border: 1px solid var(--yq-border-color);
    font-size: var(--yq-font-size-mini-mobile);
    color: var(--yq-font-color-desc);
    line-height: 1.7;
  }
  .report-title{
    margin: 14px 0 0;
    font-size: var(--yq-font-size-mini-mobile);
    font-weight: 600;
    color: var(--yq-font-color-base);
  }
  .report-empty{
    margin-top: 10px;
    padding: 20px;
    text-align: center;
    color: var(--yq-font-color-desc);
    background: #FFF;
    border-radius: 6px;
    box-shadow: 0 0 10px var(--yq-shadow);
  }
}
</style>
